import React, { useMemo, useState, useRef, useEffect } from 'react';
import { Modal, Typography, Divider, Box, Button } from '@mui/material';
import { 
  DataGrid, 
  gridClasses, 
  useGridApiRef,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector
} from '@mui/x-data-grid';
import { SaveOutlined, CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Modal as AntModal, Typography as AntTypography, Space, Divider as AntDivider, Button as AntButton, Tag } from 'antd';
import { styled } from '@mui/material/styles';
import Pagination from '@mui/material/Pagination';
import PaginationItem from '@mui/material/PaginationItem';

const { Title, Text } = AntTypography;

// Styled DataGrid with white theme
const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  border: 0,
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"Segoe UI"',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif',
    '"Apple Color Emoji"',
    '"Segoe UI Emoji"',
    '"Segoe UI Symbol"',
  ].join(','),
  WebkitFontSmoothing: 'auto',
  letterSpacing: 'normal',
  '& .MuiDataGrid-columnsContainer': {
    backgroundColor: '#fafafa',
  },
  '& .MuiDataGrid-iconSeparator': {
    display: 'none',
  },
  '& .MuiDataGrid-columnHeader, .MuiDataGrid-cell': {
    borderRight: '1px solid #f0f0f0',
  },
  '& .MuiDataGrid-columnsContainer, .MuiDataGrid-cell': {
    borderBottom: '1px solid #f0f0f0',
  },
  '& .MuiDataGrid-cell': {
    color: 'rgba(0,0,0,.85)',
  },
  '& .MuiPaginationItem-root': {
    borderRadius: 0,
  },
}));

// Custom pagination component
function CustomPagination() {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageCount = useGridSelector(apiRef, gridPageCountSelector);

  return (
    <Pagination
      color="primary"
      variant="outlined"
      shape="rounded"
      size="small"
      page={page + 1}
      count={pageCount}
      renderItem={(props) => <PaginationItem {...props} disableRipple />}
      onChange={(event, value) => apiRef.current.setPage(value - 1)}
      sx={{ 
        marginTop: '5px',
        display: 'flex',
        justifyContent: 'center',
        '& .MuiPaginationItem-root': {
          minWidth: '24px',
          height: '24px',
          fontSize: '0.75rem'
        }
      }}
    />
  );
}

// Page size constant
const PAGE_SIZE = 100;

/**
 * Component to show a summary of all changes before saving
 */
const ChangesSummaryModal = ({ 
  visible, 
  onClose, 
  onConfirm,
  originalBonliv,
  modifiedBonliv,
  originalItems,
  modifiedItems,
  loading = false,
  lineIdentity = false
}) => {
  // Add sortedInfo state
  const [sortedInfo, setSortedInfo] = useState({
    field: 'CODE_ART',
    sort: 'asc'
  });
  
  // Pagination models for each grid
  const [headerPaginationModel, setHeaderPaginationModel] = useState({
    pageSize: PAGE_SIZE,
    page: 0,
  });
  
  const [modifiedPaginationModel, setModifiedPaginationModel] = useState({
    pageSize: PAGE_SIZE,
    page: 0,
  });
  
  const [addedPaginationModel, setAddedPaginationModel] = useState({
    pageSize: PAGE_SIZE,
    page: 0,
  });
  
  const [deletedPaginationModel, setDeletedPaginationModel] = useState({
    pageSize: PAGE_SIZE,
    page: 0,
  });
  
  // Grid API refs and focus tracking
  const headerApiRef = useGridApiRef();
  const modifiedApiRef = useGridApiRef();
  const addedApiRef = useGridApiRef();
  const deletedApiRef = useGridApiRef();
  const [focusedRowId, setFocusedRowId] = useState(null);
  const [focusedGridId, setFocusedGridId] = useState(null);
  
  // More reliable focus tracking using DOM observation
  const gridRef = useRef(null);
  
  // Set up a mutation observer to track focus changes in the grid
  useEffect(() => {
    if (!gridRef.current) return;
    
    // Function to handle focus change detection
    const handleFocusChange = (mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'attributes' && 
            mutation.attributeName === 'tabindex' && 
            document.activeElement && 
            document.activeElement.closest('.MuiDataGrid-row')) {
          
          const rowElement = document.activeElement.closest('.MuiDataGrid-row');
          if (rowElement && rowElement.dataset.id) {
            setFocusedRowId(rowElement.dataset.id);
            
            // Determine which grid is focused
            const gridContainer = rowElement.closest('.MuiDataGrid-root');
            if (gridContainer) {
              if (gridContainer.classList.contains('header-grid')) {
                setFocusedGridId('header');
              } else if (gridContainer.classList.contains('modified-grid')) {
                setFocusedGridId('modified');
              } else if (gridContainer.classList.contains('added-grid')) {
                setFocusedGridId('added');
              } else if (gridContainer.classList.contains('deleted-grid')) {
                setFocusedGridId('deleted');
              }
            }
          }
        }
      }
    };
    
    // Create a mutation observer to watch for focus changes
    const observer = new MutationObserver(handleFocusChange);
    
    // Start observing the grid for attribute changes
    observer.observe(gridRef.current, { 
      attributes: true, 
      subtree: true, 
      attributeFilter: ['tabindex'] 
    });
    
    // Cleanup
    return () => {
      observer.disconnect();
    };
  }, []);
  
  // Additional keyboard event listener for more reliable focus tracking
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
        // Use setTimeout to ensure this runs after the grid's internal handlers
        setTimeout(() => {
          const focusedElement = document.querySelector('.MuiDataGrid-cell:focus');
          if (focusedElement) {
            const rowElement = focusedElement.closest('.MuiDataGrid-row');
            if (rowElement && rowElement.dataset.id) {
              setFocusedRowId(rowElement.dataset.id);
              
              // Determine which grid is focused
              const gridContainer = rowElement.closest('.MuiDataGrid-root');
              if (gridContainer) {
                if (gridContainer.classList.contains('header-grid')) {
                  setFocusedGridId('header');
                } else if (gridContainer.classList.contains('modified-grid')) {
                  setFocusedGridId('modified');
                } else if (gridContainer.classList.contains('added-grid')) {
                  setFocusedGridId('added');
                } else if (gridContainer.classList.contains('deleted-grid')) {
                  setFocusedGridId('deleted');
                }
              }
            }
          }
        }, 0);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Calculate header field changes
  const headerChanges = useMemo(() => {
    if (!originalBonliv || !modifiedBonliv) return [];
    
    const changes = [];
    const fieldsToCheck = [
      { key: 'MODE_PAIE', label: 'M.PAIE' },
      { key: 'MODELIV', label: 'M.Liv' },
      { key: 'REMARQUE', label: 'Remarque' },
      { key: 'CODE_COM', label: 'Code Com' },
      { key: 'REF_DEV', label: 'Référence' },
      { key: 'REF_FAC', label: 'Réf. Facture' }, 
      { key: 'REMISEG', label: 'REMISE G' }
    ];
    
    fieldsToCheck.forEach(field => {
      if (originalBonliv[field.key] !== modifiedBonliv[field.key]) {
        changes.push({
          id: field.key,
          field: field.label,
          original: originalBonliv[field.key] || 'N/A',
          modified: modifiedBonliv[field.key] || 'N/A'
        });
      }
    });
    
    return changes;
  }, [originalBonliv, modifiedBonliv]);
  
  // Calculate item changes
  const itemChanges = useMemo(() => {
    if (!originalItems || !modifiedItems) return { modified: [], added: [], deleted: [] };
    
    const modified = [];
    const originalMap = new Map();
    const modifiedMap = new Map();
    
    // Create maps for faster lookups
    originalItems.forEach(item => originalMap.set(lineIdentity ? item.id : item.CODE_ART, item));
    modifiedItems.forEach(item => modifiedMap.set(lineIdentity ? item.id : item.CODE_ART, item));
    
    // Find modified items
    modifiedItems.forEach(modifiedItem => {
      const originalItem = originalMap.get(lineIdentity ? modifiedItem.id : modifiedItem.CODE_ART);
      if (originalItem) {
        const changes = [];
        
        // Check for changes in each field
        (lineIdentity ? ['QTE', 'PA_HT', 'PA_TTC', 'TVA', 'REMISE'] : ['QTE', 'qte_prepare', 'qte_controle', 'qte_liv', 'PV_TTC', 'REMISE']).forEach(field => {
          if (originalItem[field] !== modifiedItem[field]) {
            changes.push({
              field,
              original: originalItem[field] === -1 ? 'N/A' : originalItem[field],
              modified: modifiedItem[field] === -1 ? 'N/A' : modifiedItem[field]
            });
          }
        });
        
        if (changes.length > 0) {
          modified.push({
            id: lineIdentity ? modifiedItem.id : modifiedItem.CODE_ART,
            CODE_ART: modifiedItem.CODE_ART,
            ARTICLE: modifiedItem.ARTICLE,
            changes
          });
        }
      }
    });
    
    // Find added items
    const added = modifiedItems
      .filter(item => !originalMap.has(lineIdentity ? item.id : item.CODE_ART))
      .map(item => {
        // Safely parse numeric values with fallbacks
        const qty = item.QTE === -1 || item.QTE === undefined ? 0 : Number(item.QTE) || 0;
        const price = Number(lineIdentity ? item.PA_TTC : item.PV_TTC) || 0;
        const discount = item.REMISE === undefined ? 0 : (Number(item.REMISE) || 0);
        return {
          ...item,
          id: lineIdentity ? item.id : item.CODE_ART,
          total: qty * price * (1 - discount / 100)
        };
      });
    
    // Find deleted items
    const deleted = originalItems
      .filter(item => !modifiedMap.has(lineIdentity ? item.id : item.CODE_ART))
      .map(item => ({
        ...item,
        id: lineIdentity ? item.id : item.CODE_ART
      }));
    
    return { modified, added, deleted };
  }, [originalItems, modifiedItems, lineIdentity]);
  
  // Calculate total change in amounts
  const amountChanges = useMemo(() => {
    if (!originalBonliv || !modifiedBonliv) return { ht: 0, ttc: 0 };
    
    return {
      ht: {
        original: originalBonliv.MNT_HT || 0,
        modified: modifiedBonliv.MNT_HT || 0,
        difference: (modifiedBonliv.MNT_HT || 0) - (originalBonliv.MNT_HT || 0)
      },
      ttc: {
        original: originalBonliv.MNT_TTC || 0,
        modified: modifiedBonliv.MNT_TTC || 0,
        difference: (modifiedBonliv.MNT_TTC || 0) - (originalBonliv.MNT_TTC || 0)
      }
    };
  }, [originalBonliv, modifiedBonliv]);
  
  // Header changes columns for MUI DataGrid
  const headerColumns = [
    {
      field: 'field',
      headerName: 'Champ',
      width: 150,
    },
    {
      field: 'original',
      headerName: 'Valeur Originale',
      width: 250,
      renderCell: (params) => (
        <Typography style={{ textDecoration: 'line-through' }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'modified',
      headerName: 'Nouvelle Valeur',
      width: 250,
      renderCell: (params) => (
        <Typography style={{ fontWeight: 'bold', color: '#52c41a' }}>
          {params.value}
        </Typography>
      ),
    },
  ];
  
  // Modified items columns
  const modifiedItemsColumns = [
    {
      field: 'CODE_ART',
      headerName: 'Code',
      width: 150,
      sortable: true,
    },
    {
      field: 'ARTICLE',
      headerName: 'Article',
      width: 550,
      renderCell: (params) => (
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {params.value}
        </div>
      ),
    },
    {
      field: 'changes',
      headerName: 'Modifications',
      width: 170,
      renderCell: (params) => (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {params.value.map((change, index) => (
            <Tag 
              key={index} 
              color="purple"
              style={{
                fontSize: '12px',
                marginBottom: '2px',
                padding: '1px 2px'
              }}
            >
              {change.field}: <span style={{ textDecoration: 'line-through' }}>{change.original}</span>
              {' → '}
              <span style={{ fontWeight: 'bold' }}>{change.modified}</span>
            </Tag>
          ))}
        </div>
      ),
    },
  ];

  // Added items columns for MUI DataGrid
  const addedItemsColumns = [
    {
      field: 'CODE_ART',
      headerName: 'Code',
      width: 110,
      sortable: true,
    },
    {
      field: 'ARTICLE',
      headerName: 'Article',
      width: 300,
      renderCell: (params) => (
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {params.value}
        </div>
      ),
    },
    {
      field: 'QTE',
      headerName: 'Qté',
      width: 80,
      align: 'right',
      valueFormatter: (params) => 
        params.value === -1 ? 'N/A' : params.value,
    },
    {
      field: 'total',
      headerName: 'Total',
      width: 90,
      align: 'right',
      valueFormatter: (params) => {
        // Handle undefined, null or NaN values
        if (params.value === undefined || params.value === null || isNaN(params.value)) {
          return '0.00';
        }
        return params.value.toFixed(2);
      },
    },
    {
      field: 'info',
      headerName: '',
      width: 50,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <InfoCircleOutlined style={{ color: '#1976d2' }} />
        </Box>
      ),
    }
  ];

  // Deleted items columns for MUI DataGrid
  const deletedItemsColumns = [
    {
      field: 'CODE_ART',
      headerName: 'Code',
      width: 110,
      sortable: true,
    },
    {
      field: 'ARTICLE',
      headerName: 'Article',
      width: 400,
      renderCell: (params) => (
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {params.value}
        </div>
      ),
    },
    {
      field: 'QTE',
      headerName: 'Qté',
      width: 120,
      align: 'right',
      valueFormatter: (params) => 
        params.value === -1 ? 'N/A' : params.value,
    },
  ];

  // Common MUI DataGrid style
  const commonGridStyle = {
    height: 200,
    width: '100%',
    flex: 1,
    cursor: 'pointer',
    border: 'none',
    fontSize: '14px',
    overflowY: 'auto',
    '& .MuiDataGrid-row': {
      // Auto height handled by getRowHeight
    },
    [`& .${gridClasses.cell}`]: {
      py: 1,
      padding: '3px 10px',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '& .MuiDataGrid-cell:focus': {
      outline: 'solid #000000 1px',
    },
    '& .MuiDataGrid-columnHeader': {
      padding: '0 10px',
      fontWeight: 'bold',
    },
    '& .focused-row': {
      borderTop: '2px solid black',
      borderBottom: '2px solid black',
    }
  };

  return (
    <AntModal
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>Résumé des Modifications</Title>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      width={960}
      style={{ top: 20 }}
      bodyStyle={{ 
        height: 500, 
        overflow: 'auto', 
        paddingRight: 8,
        overflowX: 'hidden'
      }}
      maskStyle={{ overflow: 'hidden' }}
      footer={[
        <AntButton key="cancel" onClick={onClose} icon={<CloseCircleOutlined />}>
          Annuler
        </AntButton>,
        <AntButton 
          key="save" 
          type="primary" 
          onClick={onConfirm} 
          loading={loading}
          icon={<SaveOutlined />}
        >
          Confirmer & Enregistrer
        </AntButton>
      ]}
    >
      <div ref={gridRef}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {/* Header changes */}
          {headerChanges.length > 0 && (
            <>
              <AntDivider orientation="left" orientationMargin={0} style={{ margin: '4px 0' }}>
                <Text strong>Modifications d'En-tête</Text>
              </AntDivider>
              
              <div style={{ height: 200, width: '100%' }}>
                <StyledDataGrid
                  apiRef={headerApiRef}
                  className="header-grid"
                  rows={headerChanges}
                  columns={headerColumns}
                  disableColumnMenu
                  disableColumnFilter
                  disableColumnSelector
                  disableDensitySelector
                  autoHeight={false}
                  getRowHeight={() => 'auto'}
                  getEstimatedRowHeight={() => 35}
                  headerHeight={35}
                  onCellClick={(params) => {
                    setFocusedRowId(params.id);
                    setFocusedGridId('header');
                  }}
                  getRowClassName={(params) => 
                    focusedRowId === params.id && focusedGridId === 'header' ? 'focused-row' : ''
                  }
                  paginationModel={headerPaginationModel}
                  onPaginationModelChange={setHeaderPaginationModel}
                  pageSizeOptions={[PAGE_SIZE]}
                  slots={{
                    pagination: CustomPagination,
                  }}
                  sx={commonGridStyle}
                />
              </div>
            </>
          )}
          
          {/* Modified items */}
          {itemChanges.modified.length > 0 && (
            <>
              <AntDivider orientation="left" orientationMargin={0} style={{ margin: '4px 0' }}>
                <Text strong>Articles Modifiés</Text>
              </AntDivider>
              
              <div style={{ height: 400, width: '100%' }}>
                <StyledDataGrid
                  apiRef={modifiedApiRef}
                  className="modified-grid"
                  rows={itemChanges.modified}
                  columns={modifiedItemsColumns}
                  disableColumnMenu
                  disableColumnFilter
                  disableColumnSelector
                  disableDensitySelector
                  autoHeight={false}
                  getRowHeight={() => 'auto'}
                  getEstimatedRowHeight={() => 35}
                  headerHeight={35}
                  onCellClick={(params) => {
                    setFocusedRowId(params.id);
                    setFocusedGridId('modified');
                  }}
                  getRowClassName={(params) => 
                    focusedRowId === params.id && focusedGridId === 'modified' ? 'focused-row' : ''
                  }
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'CODE_ART', sort: 'asc' }],
                    },
                  }}
                  paginationModel={modifiedPaginationModel}
                  onPaginationModelChange={setModifiedPaginationModel}
                  pageSizeOptions={[PAGE_SIZE]}
                  slots={{
                    pagination: CustomPagination,
                  }}
                  sx={{
                    ...commonGridStyle,
                    height: 400, // Override just for modified items grid
                  }}
                />
              </div>
            </>
          )}
          
          {/* Added items */}
          {itemChanges.added.length > 0 && (
            <>
              <AntDivider orientation="left" orientationMargin={0} style={{ margin: '4px 0' }}>
                <Text strong>Articles Ajoutés</Text>
              </AntDivider>
              
              <div style={{ height: 200, width: '100%' }}>
                <StyledDataGrid
                  apiRef={addedApiRef}
                  className="added-grid"
                  rows={itemChanges.added}
                  columns={addedItemsColumns}
                  disableColumnMenu
                  disableColumnFilter
                  disableColumnSelector
                  disableDensitySelector
                  autoHeight={false}
                  getRowHeight={() => 'auto'}
                  getEstimatedRowHeight={() => 35}
                  headerHeight={35}
                  onCellClick={(params) => {
                    setFocusedRowId(params.id);
                    setFocusedGridId('added');
                  }}
                  getRowClassName={(params) => 
                    focusedRowId === params.id && focusedGridId === 'added' ? 'focused-row' : ''
                  }
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'CODE_ART', sort: 'asc' }],
                    },
                  }}
                  paginationModel={addedPaginationModel}
                  onPaginationModelChange={setAddedPaginationModel}
                  pageSizeOptions={[PAGE_SIZE]}
                  slots={{
                    pagination: CustomPagination,
                  }}
                  sx={commonGridStyle}
                />
              </div>
            </>
          )}
          
          {/* Deleted items */}
          {itemChanges.deleted.length > 0 && (
            <>
              <AntDivider orientation="left" orientationMargin={0} style={{ margin: '4px 0' }}>
                <Text strong>Articles Supprimés</Text>
              </AntDivider>
              
              <div style={{ height: 200, width: '100%' }}>
                <StyledDataGrid
                  apiRef={deletedApiRef}
                  className="deleted-grid"
                  rows={itemChanges.deleted}
                  columns={deletedItemsColumns}
                  disableColumnMenu
                  disableColumnFilter
                  disableColumnSelector
                  disableDensitySelector
                  autoHeight={true}
                  getRowHeight={() => 'auto'}
                  getEstimatedRowHeight={() => 35}
                  headerHeight={35}
                  onCellClick={(params) => {
                    setFocusedRowId(params.id);
                    setFocusedGridId('deleted');
                  }}
                  getRowClassName={(params) => 
                    focusedRowId === params.id && focusedGridId === 'deleted' ? 'focused-row' : ''
                  }
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'CODE_ART', sort: 'asc' }],
                    },
                  }}
                  paginationModel={deletedPaginationModel}
                  onPaginationModelChange={setDeletedPaginationModel}
                  pageSizeOptions={[PAGE_SIZE]}
                  slots={{
                    pagination: CustomPagination,
                  }}
                  sx={commonGridStyle}
                />
              </div>
            </>
          )}
          
          {/* Amount changes */}
          {(amountChanges.ht.difference !== 0 || amountChanges.ttc.difference !== 0) && (
            <>
              <AntDivider orientation="left" orientationMargin={0} style={{ margin: '4px 0' }}>
                <Text strong>Impact Financier</Text>
              </AntDivider>
              
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div>
                  <Text strong>TOTAL HT: </Text>
                  <Text delete>{amountChanges.ht.original.toFixed(2)}</Text>
                  <Text type="success" strong> → {amountChanges.ht.modified.toFixed(2)}</Text>
                  <span 
                    style={{ 
                      marginLeft: 8,
                      backgroundColor: amountChanges.ht.difference > 0 ? '#52c41a' : '#ff4d4f',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: '0.85rem'
                    }}
                  >
                    {amountChanges.ht.difference > 0 ? '+' : ''}{amountChanges.ht.difference.toFixed(2)}
                  </span>
                </div>
                
                <div>
                  <Text strong>TOTAL TTC: </Text>
                  <Text delete>{amountChanges.ttc.original.toFixed(2)}</Text>
                  <Text type="success" strong> → {amountChanges.ttc.modified.toFixed(2)}</Text>
                  <span
                    style={{ 
                      marginLeft: 8,
                      backgroundColor: amountChanges.ttc.difference > 0 ? '#52c41a' : '#ff4d4f',
                      color: 'white',
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: '0.85rem'
                    }}
                  >
                    {amountChanges.ttc.difference > 0 ? '+' : ''}{amountChanges.ttc.difference.toFixed(2)}
                  </span>
                </div>
              </div>
            </>
          )}
        </Space>
      </div>
    </AntModal>
  );
};

export default ChangesSummaryModal;